// External dependencies.
import { LitElement, html } from "lit";
import { __ } from "@wordpress/i18n";
import swal from "sweetalert";
import $ from "jquery";

/**
 * AppZipcodeVerifyForm component class.
 *
 * @since 0.14.0
 * @class AppZipcodeVerifyForm
 * @extends {LitElement}
 */
class AppZipcodeVerifyForm extends LitElement {
    /**
     * Creates an instance of AppZipcodeVerifyForm.
     * 
     * @since 0.14.0
     * @memberof AppZipcodeVerifyForm
     */
    constructor() {
        super();
        this.isSubmitting = false;
        this.errors = {};
        this.handleSubmit = this.handleSubmit.bind( this );
        this.handleSuccess = this.handleSuccess.bind( this );
        this.handleError = this.handleError.bind( this );
    }

    /**
     * Inits the component's properties.
     *
     * @since 0.14.0
     * @readonly
     * @static
     * @memberof AppZipcodeVerifyForm
     */
    static get properties() {
        return {
            nonce: { type: String },
            isSubmitting: { type: Boolean },
            errors: { type: Object },
        };
    }

    /**
     * Handles the form element submit event.
     *
     * @since 0.14.0
     * @param object e
     * @memberof AppZipcodeVerifyForm
     */
    handleSubmit( e ) {
        e.preventDefault();
        this.isSubmitting = true;
        const form = e.target;
        const url = form.getAttribute( 'action' );
        const formdata = new FormData( form );

        fetch( url, {
            method: 'POST',
            body: formdata,
        } )
            .then( res => res.json() )
            .then( this.handleSuccess )
            .catch( this.handleError );
    }

    /**
     * Handles the form success action.
     *
     * @since 0.14.0
     * @param object res
     * @memberof AppZipcodeVerifyForm
     */
    handleSuccess( res ) {
        this.isSubmitting = false;

        if ( res.success ) {
            this.errors = {};
            this.querySelector( 'form' ).reset();
            $( '#ts-zipcode-verify-modal' ).modal( 'hide' );

            swal( {
                title: window.app.name,
                text: res.data,
                icon: 'success',
                buttons: {
                    confirm: {
                        className: 'btn btn-ts-primary',
                    },
                },
            } );

            return;
        }

        if ( res.data.has_errors ) {
            this.errors = res.data.errors;
            return;
        }

        const text = ( 0 === res.data ) ? window.app.error_message : res.data;

        this.querySelector( 'form' ).reset();
        $( '#ts-zipcode-verify-modal' ).modal( 'hide' );
        swal( {
            title: window.app.name,
            text: text,
            icon: 'warning',
            buttons: {
                confirm: {
                    className: 'btn btn-ts-primary',
                },
            },
        } );
    }

    /**
     * Handles the form error action.
     *
     * @since 0.14.0
     * @param object err
     * @memberof AppZipcodeVerifyForm
     */
    handleError( err ) {
        console.log( err );
        this.isSubmitting = false;

        swal( {
            title: window.app.name,
            text: window.app.error_message,
            icon: 'error',
            buttons: {
                confirm: {
                    className: 'btn btn-ts-primary',
                },
            },
        } );
    }

    /**
     * Renders the component.
     * 
     * @since 0.14.0
     * @returns TemplateResult
     * @memberof AppZipcodeVerifyForm
     */
    render() {
        return html`
            <form action="${window.app.ajax_url}" method="POST" class="input-group-form" @submit="${this.handleSubmit}">
                <input type="hidden" name="_wpnonce" value="${this.nonce}">
                <input type="hidden" name="action" value="handle_zipcode_verify_form">
                <div class="form-group mb-0">
                    <label for="zipcode" class="sr-only">${__( 'Código Postal', 'tio-simon-foods' )}</label>
                    <div class="input-group input-group-lg">
                        <input type="number" name="zipcode" step="1" min="1" class="form-control text-center" placeholder="${__( 'Código Postal', 'tio-simon-foods' )}" required>
                        <div class="input-group-append">
                            <button type="submit" class="btn btn-ts-primary" ?disabled="${this.isSubmitting}">
                                ${this.isSubmitting ? __( 'Envíando...', 'tio-simon-foods' ) : __( 'Entrar', 'tio-simon-foods' )}
                            </button>
                        </div>
                    </div>
                </div>
            </form>
            ${this.errors.zipcode ? html`<div class="form-error">${this.errors.zipcode}</div>` : null}
        `;
    }

    /**
     * Renders the template in the light DOM.
     *
     * @since 0.14.0
     * @returns this
     * @memberof AppZipcodeVerifyForm
     */
    createRenderRoot() {
        return this;
    }
}

window.customElements.define( 'app-zipcode-verify-form', AppZipcodeVerifyForm );
